import { ChangeEvent } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import { c080820, c7A35CB, cFFFFFF } from '#/colors';
import { usePerpetualMarketsView } from '#/features/perpetuals/market-info/markets-context';

interface Props {
  asset: string | null;
  onChange: (asset: string) => void;
}

export default function AssetPicker(props: Props) {
  const { asset, onChange } = props;
  const { activeMarket } = usePerpetualMarketsView();

  if (activeMarket == null) return null;

  const { baseCurrency, quoteCurrency } = activeMarket;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value);
  };

  return (
    <Container>
      <Asset
        aria-label={baseCurrency}
        aria-checked={asset === baseCurrency}
        value={baseCurrency}
        onChange={handleChange}
      />
      <Asset
        aria-label={quoteCurrency}
        aria-checked={asset === quoteCurrency}
        value={quoteCurrency}
        onChange={handleChange}
      />
    </Container>
  );
}

const Container = styled.div.attrs({
  role: 'radiogroup',
  'aria-label': 'assets',
})`
  display: flex;
  height: 24px;
  font-size: 12px;
  background-color: ${c080820};
  border-radius: 5px;
`;

const Asset = styled.input.attrs({
  type: 'radio',
  name: 'asset',
})`
  appearance: none;

  cursor: pointer;
  color: ${rgba(cFFFFFF, 0.5)};
  padding: 0 6px;
  display: grid;
  font-weight: 700;
  place-items: center;
  line-height: 1px;

  /* Mobile Safari sets width and height to 16px */
  width: auto;
  height: auto;

  &[aria-checked='true'] {
    color: ${cFFFFFF};
    background-color: ${c7A35CB};
    border-radius: 5px;
  }

  &:focus-visible {
    outline: 2px solid ${rgba(c7A35CB, 0.5)};
    outline-offset: 2px;
  }

  &:after {
    content: '${(props) => props['aria-label']}';
  }
`;
